import React, { useEffect, useState, useRef } from "react";
import { array, object } from "prop-types";
import get from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
import { WithLazy } from "@quintype/components";

import { MetypeCommentsWidget } from "../../../Metype/commenting-widget";
import { generateJwtToken } from "../../../../../api/utils";
import { navigateTo } from "../../../../arrow/utils/utils";

import "./metype-comments.m.css";

const MetypeCommentsWidgetWrapper = ({ story }) => {
  const dispatch = useDispatch();
  const member = useSelector((state) => get(state, ["member"], null));
  const getState = useSelector((state) => state);
  const qtConfig = useSelector((state) => get(state, ["qt"], {}));
  const publisherAttributes = get(qtConfig, ["config", "publisher-attributes"], {});
  const metypeConfig = get(publisherAttributes, ["metypeConfig"], {});
  const isMetypeEnabled = get(publisherAttributes, ["enableMetype"], false);
  const integrationsId = get(publisherAttributes, ["metypeConfig", "integrationsId"], "");
  const [jwtToken, setJwtToken] = useState(null);
  const lastMemberIdRef = useRef(null);
  const shouldRenderMetype =
    metypeConfig &&
    Array.isArray(metypeConfig?.storyTemplate) &&
    metypeConfig?.storyTemplate?.includes(story["story-template"]);

  const fetchJwtToken = async (id) => {
    try {
      const response = await generateJwtToken(id);
      if (response) setJwtToken(response);
    } catch (error) {
      console.error("Error fetching breaking news:", error);
    }
  };

  // Scroll to comments if #comments is in URL
  useEffect(() => {
    const shouldScrollToComments = () => {
      const hash = window.location.hash;
      const urlParams = new URLSearchParams(window.location.search);
      const toastMessage = urlParams.get("toastMessage");

      return hash === "#comments" || (toastMessage && toastMessage.includes("!!"));
    };

    if (shouldScrollToComments()) {
      const scrollTimeout = setTimeout(() => {
        const commentsEl = document.getElementById("metype-comments-widget-wrapper");
        if (commentsEl) {
          commentsEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
  }, []);

  // Initial JWT fetch
  useEffect(() => fetchJwtToken(integrationsId), []);

  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        window.location.reload(); // ensures fresh state after back button
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Refetch JWT when member changes (login/logout)
  useEffect(() => {
    if (member?.id && member?.id !== lastMemberIdRef.current) {
      // Only refetch if member ID actually changed
      lastMemberIdRef.current = member.id;
      fetchJwtToken(integrationsId);
    } else if (member?.id === null || member?.id === undefined) { // member is logged out
      setJwtToken(null);
    }
  }, [member?.id]);

  useEffect(() => {
    function listenToLogin(event) {
      if (event.data && event.data.action === "metype_login") {
        const currentUrl = new URL(window.location.href);
        const cleanUrl = currentUrl.origin + currentUrl.pathname;
        const returnUrl = encodeURIComponent(cleanUrl);
        const link = `/user-login?url=${returnUrl}#comments`;
        window.location.href = link;
      }
    }
    if (typeof window !== "undefined") window.addEventListener("message", listenToLogin);
    return () => {
      if (typeof window !== "undefined") window.removeEventListener("message", listenToLogin);
    };
  }, [member]);

  return (
    <div id="metype-comments-widget-wrapper">
      {isMetypeEnabled && shouldRenderMetype && (
        <WithLazy margin="100px">
          {() => (
            <div styleName="metype-comments-container">
              <div styleName="comments-title">Comments</div>
              <MetypeCommentsWidget
                host={metypeConfig.metypeHost}
                accountId={metypeConfig.metypeAccountId}
                pageURL={story.url}
                primaryColor={metypeConfig.primaryColor}
                className={metypeConfig.className}
                jwt={jwtToken}
                fontUrl={metypeConfig.fontFamilyUrl}
                fontFamily={metypeConfig.fontFamily}
                storyId={story.id}
                message={member ? null : "log out"}
              />
            </div>
          )}
        </WithLazy>
      )}
    </div>
  );
};

MetypeCommentsWidgetWrapper.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
};

export default MetypeCommentsWidgetWrapper;
